import { Fragment, useEffect, useState } from 'react';
import { Disclosure, Menu, Transition } from '@headlessui/react';
import { Bars3Icon, XMarkIcon, Cog8ToothIcon } from '@heroicons/react/24/outline';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../wms/context/wms-context';
import fullLogo from '../../assets/images/simpleLogo.jpg';
import UptimeStatus from './uptimeStatus/UptimeStatus';
import NavItem from './NavItem';
import MobileNavItem from './MobileNavItem';
import WMSCategoryDrawer from './WMSCategoryDrawer';
import Notification from '../../components/Notification';
import config from '../../utils/config';
import logError from '../../utils/errorUtil';
import {
    filterMenuByPermissions,
    findCategoryById,
    findCategoryIdForPath,
    findTaskForPath,
    getWmsMenuTree,
    isAdminSettingsPath,
    resolveDefaultTaskRoute,
} from '../utils/wms-menu';

function classNames(...classes) {
    return classes.filter(Boolean).join(' ');
}

export default function WMSNav() {
    const { logout, currentUser, setCurrentUser } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();
    const ORIGIN = config.origin;
    const permissions = currentUser?.permissions || [];
    const defaultTask = currentUser?.defaultTask || '';

    const menu = getWmsMenuTree();
    const filteredMenu = filterMenuByPermissions(menu, permissions);
    const flatTasks = filteredMenu.flatMap((category) => category.tasks || []);
    const homePath = resolveDefaultTaskRoute(permissions, defaultTask);
    const showDrawer = filteredMenu.length > 1;
    const pathCategoryId = findCategoryIdForPath(filteredMenu, location.pathname);
    const currentTask = findTaskForPath(filteredMenu, location.pathname);
    const canSetDefaultPage = Boolean(currentTask?.permissionKey);
    const isCurrentPageDefault = Boolean(currentTask?.permissionKey && currentTask.permissionKey === defaultTask);

    const [selectedCategoryId, setSelectedCategoryId] = useState(pathCategoryId);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [savingDefaultTask, setSavingDefaultTask] = useState(false);
    const [showNotification, setShowNotification] = useState(false);
    const [notification, setNotification] = useState({ title: '', desc: '', status: '' });

    useEffect(() => {
        if (pathCategoryId) setSelectedCategoryId(pathCategoryId);
    }, [pathCategoryId]);

    useEffect(() => {
        if (!showDrawer) setDrawerOpen(false);
    }, [showDrawer]);

    // Close overlay drawer when the user types into a focused page field (e.g. after category nav).
    useEffect(() => {
        if (!drawerOpen) return;

        const onKeyDown = (event) => {
            if (event.ctrlKey || event.metaKey || event.altKey) return;
            const isTypingKey = event.key.length === 1 || event.key === 'Backspace' || event.key === 'Delete';
            if (!isTypingKey) return;

            const el = document.activeElement;
            if (!el) return;

            const tag = el.tagName;
            const isField = tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el.isContentEditable;
            if (isField) setDrawerOpen(false);
        };

        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [drawerOpen]);

    useEffect(() => {
        if (showNotification) {
            const timeoutId = setTimeout(() => setShowNotification(false), 5000);
            return () => clearTimeout(timeoutId);
        }
    }, [showNotification]);

    const isOnAdmin = isAdminSettingsPath(location.pathname);

    let selectedCategory = null;
    if (!isOnAdmin) {
        if (selectedCategoryId) {
            selectedCategory = findCategoryById(filteredMenu, selectedCategoryId);
        }
        if (!selectedCategory) {
            selectedCategory = filteredMenu[0] || null;
        }
    }

    const navTasks = selectedCategory?.tasks || [];

    const handleSelectCategory = (category) => {
        setSelectedCategoryId(category.id);
        if (category.id !== pathCategoryId && category.tasks?.[0]?.route) {
            navigate(category.tasks[0].route);
            setDrawerOpen(false);
        }
    };

    const toggleDrawer = () => setDrawerOpen((open) => !open);

    const saveCurrentPageAsDefault = async () => {
        if (!currentUser?.authentication || savingDefaultTask || !canSetDefaultPage) return;
        if (isCurrentPageDefault) return;

        setSavingDefaultTask(true);
        try {
            const response = await fetch(`${ORIGIN}/wms/login/update-default-task`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    authentication: currentUser.authentication,
                    defaultTask: currentTask.permissionKey,
                }),
            });
            if (!response.ok) throw new Error('Failed to update default task');

            const result = await response.json();
            const nextDefaultTask = result?.defaultTask ?? currentTask.permissionKey;
            localStorage.setItem('defaultTask', nextDefaultTask);
            setCurrentUser({ ...currentUser, defaultTask: nextDefaultTask });
            setNotification({
                title: 'Default page saved',
                desc: `${currentTask.name} will open when you sign in.`,
                status: 'success',
            });
            setShowNotification(true);
        } catch (error) {
            logError(error, 'saveCurrentPageAsDefault');
            setNotification({
                title: 'Error saving default page',
                desc: 'Please try again.',
                status: 'error',
            });
            setShowNotification(true);
        } finally {
            setSavingDefaultTask(false);
        }
    };

    return (
        <div className='relative'>
            {showNotification && (
                <Notification
                    showSavedNotification={showNotification}
                    setShowSavedNotification={setShowNotification}
                    title={notification?.title}
                    desc={notification?.desc}
                    status={notification?.status}
                />
            )}

            {showDrawer && drawerOpen && (
                // Clickable overlay to close the drawer
                <div
                    className='hidden fixed inset-0 z-[5] sm:block'
                    onClick={() => setDrawerOpen(false)}
                    aria-hidden='true'
                />
            )}

            {showDrawer && (
                <WMSCategoryDrawer
                    open={drawerOpen}
                    onToggle={toggleDrawer}
                    categories={filteredMenu}
                    selectedCategoryId={selectedCategory?.id}
                    onSelectCategory={handleSelectCategory}
                    homePath={homePath}
                />
            )}

            <Disclosure as='nav' className='bg-white shadow'>
                {({ open }) => (
                    <>
                        <div className='px-4 mx-auto sm:px-6 lg:px-8'>
                            <div className='flex relative justify-between h-16'>
                                <div className='flex flex-1 min-w-0'>
                                    <div
                                        className={classNames(
                                            'flex flex-shrink-0 items-center w-16',
                                            showDrawer ? 'sm:hidden' : ''
                                        )}
                                    >
                                        <Link to={homePath}>
                                            <img className='block w-auto h-8' src={fullLogo} alt='Pacific Air' />
                                        </Link>
                                    </div>
                                    <div
                                        className='hidden overflow-x-auto pl-4 max-w-full whitespace-nowrap sm:flex sm:space-x-8'
                                        style={{ scrollbarWidth: 'thin' }}
                                    >
                                        {isOnAdmin ? (
                                            <NavItem name='Admin' path='/wms/admin-settings' active />
                                        ) : (
                                            navTasks.map((task) => (
                                                <NavItem key={task.id} name={task.name} path={task.route} />
                                            ))
                                        )}
                                    </div>
                                </div>
                                <div className='hidden sm:ml-6 sm:flex sm:items-center'>
                                    <UptimeStatus />
                                    <div>
                                        <p className='pl-6 text-sm font-medium text-gray-700'>
                                            Welcome, {currentUser.authentication.userName}
                                        </p>
                                    </div>

                                    <Menu as='div' className='relative ml-3'>
                                        <div>
                                            <Menu.Button className='flex text-sm bg-white rounded-full cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-primary focus:ring-offset-2 pointer'>
                                                <span className='sr-only'>Open user menu</span>
                                                <div
                                                    type='button'
                                                    className='p-1 text-gray-400 bg-white rounded-full hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-primary focus:ring-offset-2'
                                                >
                                                    <span className='sr-only'>View notifications</span>
                                                    <Cog8ToothIcon className='w-6 h-6' aria-hidden='true' />
                                                </div>
                                            </Menu.Button>
                                        </div>
                                        <Transition
                                            as={Fragment}
                                            enter='transition ease-out duration-200'
                                            enterFrom='transform opacity-0 scale-95'
                                            enterTo='transform opacity-100 scale-100'
                                            leave='transition ease-in duration-75'
                                            leaveFrom='transform opacity-100 scale-100'
                                            leaveTo='transform opacity-0 scale-95'
                                        >
                                            <Menu.Items className='absolute right-0 z-10 py-1 mt-2 w-48 bg-white rounded-md ring-1 ring-black ring-opacity-5 shadow-lg origin-top-right focus:outline-none'>
                                                {permissions.includes('admin-settings') && (
                                                    <Menu.Item>
                                                        {({ active }) => (
                                                            <Link
                                                                to='/wms/admin-settings'
                                                                className={classNames(
                                                                    active ? 'bg-gray-100' : '',
                                                                    'block px-4 py-2 text-sm text-gray-700'
                                                                )}
                                                            >
                                                                Admin Page
                                                            </Link>
                                                        )}
                                                    </Menu.Item>
                                                )}
                                                {canSetDefaultPage && (
                                                    <Menu.Item disabled={isCurrentPageDefault || savingDefaultTask}>
                                                        {({ active, disabled }) => (
                                                            <button
                                                                type='button'
                                                                disabled={disabled}
                                                                onClick={saveCurrentPageAsDefault}
                                                                className={classNames(
                                                                    active && !disabled ? 'bg-gray-100' : '',
                                                                    'block w-full px-4 py-2 text-sm text-left text-gray-700 disabled:text-gray-400 disabled:cursor-default'
                                                                )}
                                                            >
                                                                {isCurrentPageDefault
                                                                    ? 'Current page is default'
                                                                    : 'Set as default page'}
                                                            </button>
                                                        )}
                                                    </Menu.Item>
                                                )}
                                                <Menu.Item>
                                                    {({ active }) => (
                                                        <Link
                                                            onClick={logout}
                                                            className={classNames(
                                                                active ? 'bg-gray-100' : '',
                                                                'block px-4 py-2 text-sm text-gray-700'
                                                            )}
                                                        >
                                                            Sign out
                                                        </Link>
                                                    )}
                                                </Menu.Item>
                                            </Menu.Items>
                                        </Transition>
                                    </Menu>
                                </div>
                                <div className='flex items-center -mr-2 sm:hidden'>
                                    <Disclosure.Button className='inline-flex justify-center items-center p-2 text-gray-400 rounded-md hover:bg-gray-100 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-primary'>
                                        <span className='sr-only'>Open main menu</span>
                                        {open ? (
                                            <XMarkIcon className='block w-6 h-6' aria-hidden='true' />
                                        ) : (
                                            <Bars3Icon className='block w-6 h-6' aria-hidden='true' />
                                        )}
                                    </Disclosure.Button>
                                </div>
                            </div>
                        </div>

                        <Disclosure.Panel className='sm:hidden max-h-[calc(100vh-4rem)] overflow-y-auto'>
                            <div className='pt-2 pb-3 space-y-1'>
                                {flatTasks.map((task) => (
                                    <MobileNavItem key={task.id} name={task.name} path={task.route} />
                                ))}
                                {permissions.includes('admin-settings') && (
                                    <MobileNavItem name='Admin Page' path='/wms/admin-settings' />
                                )}
                            </div>
                            <div className='pt-4 pb-3 border-t border-gray-200'>
                                {canSetDefaultPage && (
                                    <div className='mb-3 space-y-1'>
                                        <Disclosure.Button
                                            as='button'
                                            type='button'
                                            disabled={isCurrentPageDefault || savingDefaultTask}
                                            onClick={saveCurrentPageAsDefault}
                                            className='block px-4 py-2 w-full text-base font-medium text-left text-gray-500 hover:bg-gray-100 hover:text-gray-800 disabled:text-gray-400 disabled:hover:bg-transparent'
                                        >
                                            {isCurrentPageDefault ? 'Current page is default' : 'Set as default page'}
                                        </Disclosure.Button>
                                    </div>
                                )}
                                <div className='mt-3 space-y-1'>
                                    <Disclosure.Button
                                        as='a'
                                        href='#'
                                        onClick={logout}
                                        className='block px-4 py-2 text-base font-medium text-gray-500 hover:bg-gray-100 hover:text-gray-800'
                                    >
                                        Sign out
                                    </Disclosure.Button>
                                </div>
                            </div>
                        </Disclosure.Panel>
                    </>
                )}
            </Disclosure>
        </div>
    );
}
